import {BaseNotification} from './base-notification';
import * as moment from 'moment';
import {DateUtils} from '../../../date-utils';

/**
 * Notification card for an immediate business-rule apply (RLP-4281).
 *
 * Rides the same /reopt/notifications feed the bell already polls, exactly as the RARS
 * publish card does, so progress needs no extra request.
 *
 * An apply is a minutes-scale job -- it refreshes the rule matview before it can do
 * anything -- so this card is the only place a user sees that the work is still running.
 */
export class BusinessRuleApplyNotification extends BaseNotification {
    public header = 'Apply Business Rules';
    public iconClass = 'fa-gavel';

    constructor(private notificationData: any) {
        super(notificationData);
        this.title = 'Business Rules';
        this.updateHeader();
        this.buildDescription();
    }

    public getRouteInfo(): any {
        // The effect of an apply is visible on the inventory grid, over the window it
        // was applied to.
        const filters = {
            rsid: '',
            origin: '',
            destination: '',
            startDate: moment(this.notificationData.startDate, 'YYYY-MM-DD').toDate(),
            endDate: moment(this.notificationData.endDate, 'YYYY-MM-DD').toDate(),
            portfolio: []
        };
        return {page: 'inventory', filters};
    }

    public routeDisabled(): boolean {
        // Nothing has moved until the apply finishes, so there is nothing to look at.
        return this.notificationData.status !== 'Success';
    }

    private buildDescription(): void {
        const range = this.notificationData.startDate
            ? ` for ${DateUtils.convertToLocaleDateString(this.notificationData.startDate)} to `
              + `${DateUtils.convertToLocaleDateString(this.notificationData.endDate)}`
            : '';
        this.description = `Your business rule apply${range} ${this.getStatusText()}`;
    }

    private updateHeader(): void {
        if (this.notificationData.status === 'Running') {
            this.header += ' in Progress';
        }
    }

    private getStatusText(): string {
        const data = this.notificationData;
        switch (data.status) {
            case 'Requested':
                return 'is queued.';
            case 'Running':
                return 'is being applied.';
            case 'Success':
                return this.successText();
            case 'Blocked':
                // Refused before doing any work -- the reason is the whole point.
                return `was not applied: ${data.note || 'the apply was blocked'}.`;
            case 'Failed':
                return `failed. ${data.note || ''}`.trim();
            default:
                return `is ${(data.status || 'unknown').toLowerCase()}.`;
        }
    }

    /* Skipped dates are surfaced here rather than swallowed: the user asked for a
       window and is entitled to know which of it did not apply. */
    private successText(): string {
        const data = this.notificationData;
        const affected = `${data.affectedCount || 0} bucket(s) updated`;
        const skipped = (data.skippedDates || []).length;
        if (skipped > 0) {
            return `completed: ${affected}. ${skipped} departure date(s) were skipped.`;
        }
        return `completed: ${affected}.`;
    }
}
